import { formatDateOnly, type DateFormat } from '../settings/dateFormat';
import { t } from '../strings';

/**
 * 🔒 The gold certificate badge on a site's row (piece 13, the Sites tile; T3076,
 * decision 7390). The server sends the facts of each certificate that exempts
 * today, and the website and the phone read them through the functions here, so
 * the years, the state codes and the hover's words are one rule in one place.
 */
export interface TaxExemptFact {
  stateCode: string;
  /** The certificate's effective date, `YYYY-MM-DD`. */
  effectiveFrom: string;
  /** The expiry its file carries, `YYYY-MM-DD`; null where the file carries none. */
  expiresOn: string | null;
}

export interface TaxExemptBadge {
  /** The words in the pill: `PA '25–'27`, `'25–'27` or `'25`. The code leads only where the site holds two or more. */
  text: string;
  /** The hover, which is also the glyph's accessible name: Tax Exempt certificate, effective 03/14/2025, expires 12/31/2027. */
  title: string;
}

/** A year as an apostrophe and its last two digits: 2025 reads `'25`, 2100 reads `'00` and 1999 reads `'99`. */
export function taxExemptYear(day: string): string {
  return `'${day.slice(0, 4).slice(-2)}`;
}

/** The years of one certificate: effective then expiry as `'25–'27`, or the effective year alone where its file carries no expiry. */
export function taxExemptYears(fact: Pick<TaxExemptFact, 'effectiveFrom' | 'expiresOn'>): string {
  return fact.expiresOn ? `${taxExemptYear(fact.effectiveFrom)}–${taxExemptYear(fact.expiresOn)}` : taxExemptYear(fact.effectiveFrom);
}

const DAY = /^\d{4}-\d{2}-\d{2}/;

/** What a row's `taxExempt` carries, read defensively: a row from an older server, or one that sends nothing, draws no badge. */
export function taxExemptFacts(value: unknown): TaxExemptFact[] {
  if (!Array.isArray(value)) return [];
  const facts: TaxExemptFact[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { stateCode, effectiveFrom, expiresOn } = entry as Record<string, unknown>;
    if (typeof stateCode !== 'string' || typeof effectiveFrom !== 'string' || !DAY.test(effectiveFrom)) continue;
    facts.push({ stateCode, effectiveFrom: effectiveFrom.slice(0, 10), expiresOn: typeof expiresOn === 'string' && DAY.test(expiresOn) ? expiresOn.slice(0, 10) : null });
  }
  return facts;
}

/**
 * One badge per certificate that exempts today, in the order the server sent
 * them. 🔒 The state code stands before the years only where the site holds two
 * or more (decision 7390), so a site with one certificate never reads a code.
 */
export function taxExemptBadges(value: unknown, format: DateFormat): TaxExemptBadge[] {
  const facts = taxExemptFacts(value);
  const coded = facts.length > 1;
  return facts.map((fact) => {
    const dates = [`${t('sites.taxExempt.effective')} ${formatDateOnly(fact.effectiveFrom, format)}`, fact.expiresOn ? `${t('sites.taxExempt.expires')} ${formatDateOnly(fact.expiresOn, format)}` : null];
    const title = [`${coded ? `${fact.stateCode} ` : ''}${t('sites.taxExempt.title')}`, ...dates].filter(Boolean).join(', ');
    return { text: `${coded ? `${fact.stateCode} ` : ''}${taxExemptYears(fact)}`, title };
  });
}
